import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import StickyCtaBar from "../../UIElements/StickyCtaBar";
import callApi from "../../../../utility/apiCaller";
import { setUserClickData } from "../../../../utility/setUserClickData";

const AMOUNTS = [
  { id: "10-50k", amt: "₹10K – ₹50K", desc: "Small top-up" },
  { id: "50k-1l", amt: "₹50K – ₹1L", desc: "Short-term need" },
  { id: "1-3l", amt: "₹1L – ₹3L", desc: "Personal loan" },
  { id: "3-5l", amt: "₹3L – ₹5L", desc: "Higher requirement" },
  { id: "5-10l", amt: "₹5L – ₹10L", desc: "Major expense" },
  { id: "10l+", amt: "₹10L+", desc: "Large requirement" },
];

const TIMELINES = ["Immediately", "Within 7 days", "This month", "Just exploring"];

function Radio({ on }) {
  return (
    <span
      className={"w-[15px] h-[15px] rounded-full flex-none border-[1.5px] " + (on ? "border-[#12f280]" : "border-[#cfcfcf]")}
      style={on ? { background: "radial-gradient(circle at center, #12f280 0 5px, #fff 5px 7px)" } : undefined}
    />
  );
}

// IMAGE 3 — "How much do you need" (3/4). New screen; insert into landingPage.js
// between PersonalDetailsForm and ProfessionalInfoForm in the step machine.
export default function LoanAmount({ currentStep, setCurrentStep, formData = {}, setFormData, onBack, onContinue }) {
  const [amount, setAmount] = useState(formData.amountRange || null);
  const [timeline, setTimeline] = useState(formData.fundsTimeline || null);
  const [saving, setSaving] = useState(false);
  const [params] = useSearchParams();

  const aaSlice = useSelector((state) => state?.aa);
  const InternalLeadId = aaSlice?.internalLeadId;
  const user = useSelector((state) => state.app.user);

  const choose = (patch) => setFormData && setFormData((p) => ({ ...p, ...patch }));

  // Persist this step against the same lead, then advance. Best-effort: a
  // failed save must not trap the user (the amount is not required for the
  // offer fetch on the next step). The saving guard prevents double-submit
  // without changing the button's disabled UI.
  const handleContinue = async () => {
    if (saving) return;
    setSaving(true);
    setUserClickData({
      event_name: `loan-amount-multistep-pl-v1`,
      user_id: formData?.mobile || "unknown",
      affiliate_id: params.get("aff_id") || "No Aff_id found",
      utm_source: params.get("utm_source") || params.get("utm_medium"),
      utm_term: params.get("utm_term"),
    });
    try {
      if (InternalLeadId) {
        await callApi(
          `v1/lead/${InternalLeadId}/update-lead-new`,
          "post",
          {
            lead: {
              amount_required: amount || formData.amountRange || "",
              funds_timeframe: timeline || formData.fundsTimeline || "",
              steps_completed: 2,
            },
          },
          "core",
          user?.token
        );
      }
    } catch (err) {
      console.error("Failed to update lead at loan amount step:", err);
    } finally {
      setSaving(false);
    }
    if (onContinue) onContinue();
    else if (setCurrentStep) setCurrentStep(currentStep + 1);
  };

  return (
    <>
    <div className="dm-amount-v9 w-full max-w-[400px] mx-auto bg-white px-4 pb-[120px] flex flex-col font-atkinson">
      {/* Progress (3/4) */}
      <div className="flex items-center gap-[14px] pt-2 pb-1">
        <button
          type="button"
          onClick={() => (onBack ? onBack() : setCurrentStep && setCurrentStep(currentStep - 1))}
          aria-label="Go back"
          className="w-6 h-6 flex-none flex items-center justify-center"
        >
          <img src={process.env.PUBLIC_URL + "/assets/icons/left_arrow.png"} alt="" className="w-5 h-5 object-contain" />
        </button>
        <div className="flex-1 h-[7px] rounded-[6px] bg-[#c9f7e0] overflow-hidden">
          <div className="h-full bg-[#12f280] rounded-[6px]" style={{ width: "68%" }} />
        </div>
        <span className="text-[15px] text-[#3a3a3a] flex-none">3/4</span>
      </div>

      <h1 className="dm-type-header mt-[22px] text-[#1b1b1b]">How much do you need?</h1>
      <p className="text-[13px] text-[#8b8b8b] mt-3">Choose a range—we'll look for matching offers.</p>

      <div className="grid grid-cols-2 gap-[14px] mt-4">
        {AMOUNTS.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => { setAmount(a.id); choose({ amountRange: a.id }); }}
            className={
              "need-funds flex flex-col justify-center text-left rounded-[16px] px-4 h-[65px] border-[1.5px] transition-colors " +
              (amount === a.id ? "border-[#12f280] bg-black" : "border-[#e6e6e6] bg-white")
            }
          >
            <div className={"font-bold text-[16px] " + (amount === a.id ? "text-white" : "text-[#1b1b1b]")}>{a.amt}</div>
            <div className={"text-[10px] mt-[0px] " + (amount === a.id ? "text-white" : "text-[#b3b3b3]")}>{a.desc}</div>
          </button>
        ))}
      </div>

      <div className="bg-[#f6f6f6] rounded-[22px] px-[18px] py-[22px] mt-[26px]">
        <h2 className="font-bold text-[18px] text-[#1b1b1b] mb-4">When would you like the funds?</h2>
        <div className="grid grid-cols-2 gap-[14px]">
          {TIMELINES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTimeline(t); choose({ fundsTimeline: t }); }}
              className={
                "need-funds flex items-center justify-between bg-white rounded-[14px] px-4 h-[40px] text-[11px] border-[1.5px] " +
                (timeline === t ? "border-[#12f280]" : "border-[#e9e9e9]")
              }
            >
              <span>{t}</span>
              <Radio on={timeline === t} />
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-[18px]" />
    </div>
    <StickyCtaBar>
      <button
        type="button"
        disabled={!amount || !timeline}
        onClick={handleContinue}
        id="loan-details-submit-v17" className="w-full bg-black text-white font-bold text-[19px] rounded-[18px] py-[19px] active:scale-[0.99] transition-transform disabled:opacity-50"
      >
        Continue
      </button>
    </StickyCtaBar>
    </>
  );
}